import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { z } from 'zod';
import { useMutation } from '@tanstack/react-query';
import { Button, Dialog, DialogContent, DialogDescription, DialogTitle, toast, } from '@wit-support/ui';
import { Actions, SubmitButton, ZodField, ZodForm } from '@wit-support/ui-forms';
import { Merge } from 'lucide-react';
import { mergeConversation } from '../api.js';
import { mergeErrorMessage } from './merge-all-dialog.js';
const UUID_RE = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;
const URL_UUID_RE = /\/conversations\/([0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12})/i;
/**
 * Extract a UUID from a bare UUID or a URL ending with
 * /conversations/<uuid>. Returns null if nothing matches.
 */
export function parseTargetConversationId(raw) {
    const trimmed = raw.trim();
    if (!trimmed)
        return null;
    if (UUID_RE.test(trimmed))
        return trimmed.toLowerCase();
    const match = URL_UUID_RE.exec(trimmed);
    return match ? match[1].toLowerCase() : null;
}
// Flat string fields — the bare-UUID-or-URL parse and the self-merge guard
// run imperatively in onSubmit (below), so the schema stays a plain string
// (correct text/textarea widgets) and we keep the existing toast-based UX.
const MergeFormSchema = z.object({
    target: z.string().min(1, 'Enter a conversation ID or URL').meta({
        label: 'Target conversation',
        placeholder: 'Target conversation ID or URL',
        hint: 'Paste a bare UUID or a link ending in /conversations/(id).',
    }),
    note: z
        .string()
        .optional()
        .meta({ label: 'Note (optional)', widget: 'textarea', placeholder: 'Why are you merging?' }),
});
export function MergeConversationDialog({ sourceConversationId, onClose, onMerged, }) {
    const mut = useMutation({
        mutationFn: (body) => mergeConversation(sourceConversationId, body),
        onSuccess: (result) => {
            toast.success(`Слито сообщений: ${result.mergedMessageCount}.`);
            onMerged(result.targetId);
        },
        onError: (err) => {
            toast.error(mergeErrorMessage(err));
        },
    });
    async function handleSubmit(values) {
        const parsed = parseTargetConversationId(values.target);
        if (!parsed) {
            toast.error('Enter a conversation ID or URL');
            return;
        }
        if (parsed === sourceConversationId.toLowerCase()) {
            toast.error('Cannot merge a conversation into itself');
            return;
        }
        const trimmedNote = (values.note ?? '').trim();
        // mutateAsync drives the form's isSubmitting state (SubmitButton pending
        // label); the rejection is already surfaced by the mutation's onError
        // toast, so onServerError just absorbs it to avoid an unhandled throw.
        await mut.mutateAsync({ targetId: parsed, ...(trimmedNote ? { note: trimmedNote } : {}) });
    }
    return (_jsx(Dialog, { open: true, onOpenChange: (v) => (!v ? onClose() : null), children: _jsxs(DialogContent, { children: [_jsxs(DialogTitle, { className: "flex items-center gap-2", children: [_jsx(Merge, { className: "h-4 w-4", "aria-hidden": "true" }), "Merge conversation"] }), _jsx(DialogDescription, { children: "Move every message from this conversation into the target. The source becomes a tombstone that redirects to the target." }), _jsxs(ZodForm, { schema: MergeFormSchema, defaultValues: { target: '', note: '' }, onSubmit: (values) => handleSubmit(values), onServerError: () => { }, children: [_jsx(ZodField, { name: "target" }), _jsx(ZodField, { name: "note" }), _jsxs(Actions, { children: [_jsx(Button, { type: "button", variant: "ghost", onClick: onClose, children: "Cancel" }), _jsx(SubmitButton, { pendingLabel: "Merging\u2026", children: "Merge" })] })] })] }) }));
}
//# sourceMappingURL=merge-dialog.js.map